/**
 * 数値定数の型
 */
interface NumberConst {
    [key: string]: number;
}

/**
 * 文字列定数の型
 */
interface StringConst {
    [key: string]: string;
}

/**
 * 定数定義
 */
export default class SystemConst {
    /**
     * テーブル項目桁数定義
     * テーブル名（キャメルケース）+'MaxLength'とする。
     * キーの名称はテーブルのカラム名に合わせる。
     *
     * 使用例：
     * -----------------------------------------------------
     * [hoge.ts]
     * import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
     * import SystemConst from '@/shared/system-const';
     *
     * ＠Component
     * export default class hoge extends Vue {
     *     // テンプレート(.vue)で使用する場合はメンバ変数に入れて使用する。
     *     private systemConst: typeof SystemConst = SystemConst;
     *     // tsファイルの中ではSystemConstを直接参照できます。
     *     private groupNameMaxLength: number = SystemConst.groupMaxLength.groupName;
     * }
     * -----------------------------------------------------
     */

    /**
     * ユーザー情報最大桁数
     */
    public static readonly USER_MAX_LENGTH: NumberConst = {
        IDENTIFICATION_CODE: 30,        // 識別コード
        LAST_NAME: 60,                  // 姓
        FIRST_NAME: 60,                 // 名
        LAST_NAME_KANA: 256,            // 姓（かな）
        FIRST_NAME_KANA: 256,           // 名（かな）
        MAIL: 64,                       // メールアドレス部
        MAIL_ADDRESS: 255,              // メールアドレス
        PASSWORD: 100,                  // パスワード
        RE_PASSWORD: 100,               // 再入力パスワード
        POST_NAME: 256,                 // 職
    };

    /**
     * ユーザー登録/更新情報最小桁数
     */
    public static readonly USER_MIN_LENGTH: NumberConst = {
        MAIL: 1,                        // メールアドレス
        PASSWORD: 8,                    // パスワード
        RE_PASSWORD: 8                  // 再入力パスワード
    };

    /**
     * フィルター最大桁数
     * 
     */
    public static readonly FILTER_MAX_LENGTH: NumberConst = {
        FILTER_LENGTH: 100,  // テキストボックス用
        NUM_RANGE_LENGTH: 4  // 範囲指定用
    };

    /**
     * アカウント区分の値定義
     */
    public static readonly ACCOUNT_DIVISION: StringConst = {
        NOT_ENABLED: '',                                    // 未入力
        TEACHER: 'a1',                                      // （個人）教職員
        TEACHER_ORGANIZATION_MANAGER: '教職員（組織管理者）', // （個人）教職員（組織管理者）
        STUDENT: 'b1',                                      // （個人）生徒
        GENERAL: 'b2',                                      // （個人）一般
        REPRESENTATIVE: 'c1',                               // （組織）組織代表
        ADMIN: 's1'                                         // （管理）システム管理者
    };

    /**
     * アカウント区分名称の値定義
     */
    public static readonly ACCOUNT_DIVISION_NAME: StringConst = {
        TEACHER: '（個人）教職員アカウント',
        STUDENT: '（個人）生徒アカウント',
        GENERAL: '（個人）一般アカウント',
        REPRESENTATIVE: '（組織）組織代表アカウント',
        ADMIN: '（管理）システム管理者アカウント'
    };

    /**
     * メールアドレス（選択）の値定義
     */
    public static readonly MAIL_SET: StringConst = {
        AUTOFILL_ADDRESS: '1',          // 自動入力
        INPUT_DIRECT_ADDRESS: '0'       // 直接入力
    };

    /**
     * パスワード（選択）の値定義
     */
    public static readonly PASSWORD_SET: StringConst = {
        AUTOFILL_PASSWORD: '0',         // 自動入力
        INPUT_DIRECT_PASSWORD: '1'      // 直接入力
    };

    /**
     * 状態の値定義
     */
    public static readonly ENABLED_NAME: StringConst = {
        VALID: '有効',
        INVALID: '無効'
    };

    /**
     * 組織区分定義
     * キーの名称は組織の所属に合わせる。
     */
    public static readonly ORGANIZATION_DIVISION: NumberConst = {
        ORG_TYPE: 0,         // 組織型
        ORG: 1,              // 組織
        ALL_USER: 99         // 全組織
    };

    /**
     * 組織型定義
     */
    public static readonly ORGANIZATION_MANAGEMENT_ID: NumberConst = {
        ORG_TYPE: 0          // 組織型
    };

    /**
     * 処理状況ステータス
     */
    public static readonly PROCESSING_STATUS: NumberConst = {
        SUCCESS: 0,             // 正常終了
        WAIT: 1,                // 処理待ち
        PROCESSING: 2,          // 処理中
        ERROR: 99               // エラー
    };

    /**
     * 処理状況ステータス名称
     */
    public static readonly PROCESSING_STATUS_NAME: StringConst = {
        SUCCESS: '正常終了',
        WAIT: '処理待ち',
        PROCESSING: '実行中',
        ERROR: '異常終了'
    };

    /**
     * 全組織表示時の組織ID
     */
    public static readonly ORGANIZATION_ALL_USER: string = '99999999';

    /**
     * 検索方法
     */
    public static readonly USER_SEARCH_METHOD: StringConst = {
        ALL_USER: '0',
        SELECT_USER: '1'
    };

    /**
     * 組織ツリーで選択された組織の区分
     */
    public static readonly SELECT_TREE_DIVISION: NumberConst = {
        ALL: 0,
        ORGANIZATION_TYPE: 1,
        ORGANIZATION: 2
    };

    /**
     * 組織ツリーの幅の調整値定義
     */
    public static readonly ORG_TREE_SIZE: NumberConst = {
        WIDTH: 500,
        HEIGHT: 380
    };

    /**
     * 一覧画面のテーブルの高さ調整値定義
     */
    public static readonly TABLE_HEIGHT: NumberConst = {
        USER_LIST: 350,                               // ユーザー一覧
        ORGANIZATION_LIST: 350,                       // 組織一覧
        BULK_ADD_USERS: 500,                          // 一括登録チェック結果
        PROCESSING_STATUS_LIST: 300,                  // ユーザー・組織処理状況一覧
        PROCESSING_STATUS_USERS_DETAIL: 335,          // ユーザー処理状況詳細
        PROCESSING_STATUS_ORGANIZATIONS_DETAIL: 220,  // 組織処理状況詳細
        PROCESSING_REQUESTS_LIST: 290,                // 処理依頼一覧
        CATEGORY_LIST: 390,                           // カテゴリ一覧
        CURRICULUM_LIST: 425,                         // カテゴリ一覧（教材DB：教科設定）
        SUBJECT_LIST: 440,                            // カテゴリ一覧（教材DB：科目設定）
        AFFILIATION_CODE_LIST: 385,                   // 所属コード変換
        PERSONNEL_CHANGE_RESULT_LIST: 450,            // 人事異動データチェック結果
        NOTIFICATION_DATA_RESULT: 435                 // 通知データ作成チェック結果
    }

    /**
     * 登録、編集、削除画面のフォームの高さ調整値定義
     */
    public static readonly FORM_HEIGHT: NumberConst = {
        HEIGHT: 305,
        NOTIFICATION_FILE: 335,                       // 年度更新通知データ作成ファイル選択
        PARAMETER: 325                                // パラメータ管理（パラメータ設定）
    }

    /**
     * 組織ツリーのアイコン定義
     */
    public static readonly TREE_ICON: StringConst = {
        home: 'mdi-home-variant',
        folder: 'mdi-folder-network',
        list: 'mdi-view-list'
    };

    /**
     * テーブルのデータ取得中に表示するテキストの定義
     */
    public static readonly TABLE_LOADING_TEXT: string = 'データ取得中です';

    /**
     * 組織登録画面最大桁数定義
     * キーの名称はテーブルのカラム名に合わせる。
     */
    public static readonly ORGANIZATION_ADD_MAX_LENGTH: NumberConst = {
        AFFILIATION_CODE: 256,          // 所属コード
        DISPLAY_ORDER: 5,               // 表示順              
        ORGANIZATION_NAME: 256,         // 組織名称
        ORGANIZATION_NAME_KANA: 512,    // 組織名称(かな)
        DOMAIN: 253,                    // 組織ドメイン
        REPRESENT_MAIL: 254,            // 代表メールアドレス
        REMARKS: 2000                   // 備考
    };

    /**
     * リスト表示件数定義
     */
    public static readonly LIST_LINES_PER_PAGE: number[] = [10, 50, 100];

    /**
     * APIパラメータ共通定義（ソート順）
     */
    public static readonly SORT_ORDER: StringConst = {
        ASC: 'asc',                             // 昇順
        DESC: 'desc',                           // 降順
    };

    /**
     * APIパラメータ共通定義（ソート順）
     */
    public static readonly SORT_ORDER_MAPPING: string[] = [
        SystemConst.SORT_ORDER.ASC,
        SystemConst.SORT_ORDER.DESC
    ];

    /**
     * APIパラメータ共通定義（一覧取得系APIの共通パラメータ）
     */
    public static readonly LIST_API_PARAM: StringConst = {
        SORT_KEY: 'sortKey',                    // ソートキー
        SORT_ORDER: 'sortOrder',                // ソート順
        PAGE: 'page',                           // ページ番号
        ITEMS_PER_PAGE: 'itemsPerPage',         // 1ページあたりの行数
    };

    /**
     * APIパラメータ共通定義（Excel出力系APIの共通パラメータ）
     */
    public static readonly EXCEL_API_PARAM: StringConst = {
        USERS_FILE: 'ユーザー一覧.xlsx',                         // ユーザー一覧ファイル名
        BULK_ADD_USERS_FILE: '一括登録ファイル.xlsx',            // 一括登録用ファイル名
        BULK_ADD_USERS_NORMAL_RESULTS_FILE: '正常データ.xlsx',   // (一括登録)正常データファイル名
        BULK_ADD_USERS_ERROR_RESULTS_FILE: 'エラーデータ.xlsx',  // (一括登録)エラーデータファイル名
        BULK_ADD_USERS_PASSWORD_FILE: 'パスワード情報.xlsx',     // (一括登録)パスワード情報ファイル名
        PROCESSING_STATUS_USERS_FILE: 'ユーザー処理状況.xlsx',   // 処理状況ファイル名
        ORGANIZATIONS_FILE: '組織一覧.xlsx'                      // 組織一覧ファイル名
    };

    /**
     * 処理依頼確認画面最大桁数定義
     */
    public static readonly PROCESSING_REQUESTS_MAX_LENGTH: NumberConst = {
        REPLY_COMMENT: 600      // 返信コメント
    }

    /**
     * 年度更新の通知データ作成時ファイル区分
     */
    public static readonly FILE_DIVISION: StringConst = {
        PASSWORD: 'パスワード',
        ADDRESSEE: '宛名',
        TEMPLATE: '雛形'
    }

    /**
     * 機能選択プルダウン定義
     */
    public static readonly FUNCTION_PULLDOWN_ITEMS = [
        { text: '公文書', value: '1' },
        { text: 'お知らせ', value: '2' },
        { text: '重要情報のお知らせ', value: '3' },
        { text: '教材データベース', value: '4' },
        { text: '教育用コンテンツ', value: '5' },
        { text: 'システム管理', value: '6' },
        { text: '組織内管理', value: '7' },
        { text: '共通（システム管理・組織内管理）', value: '8' }
    ];

    /**
     * 選択した機能選択プルダウン定義
     * 上記「FUNCTION_PULLDOWN_ITEMS」のvalueとリンクさせる
     */
    public static readonly SELECT_FUNCTION_PULLDOWN_ITEMS: StringConst = {
        OFFICIAL_DOCUMENTS: '1',     // 公文書
        INFORMATION: '2',            // お知らせ
        IMPORTANT_INFORMATION: '3',  // 重要情報のお知らせ
        MATERIAL_DB: '4',            // 教材データベース
        CONTENT: '5',                // 教育用コンテンツ
        ADMIN: '6',                  // システム管理
        ORG_ADMIN: '7',              // 組織内管理
        COMMON: '8'                  // 共通（システム管理・組織内管理）
    }

    /**
     * パラメータマスタのサービスコード定義
     */
    public static readonly SERVICE_CODE: StringConst = {
        OFFICIAL_DOCUMENTS: 'official_documents',        // 公文書
        INFORMATION: 'information',                      // お知らせ
        IMPORTANT_INFORMATION: 'important_information',  // 重要情報のお知らせ
        MATERIAL_DB: 'material_db',                      // 教材DB
        CONTENT: 'content'                               // 教育用コンテンツ
    };

    /**
     * パラメータ設定の機能定義
     * 
     * システム管理など一つの機能で複数の管理機能を持つ場合使用する
     */
    public static readonly PARAMETER_FUNCTION_CODE: StringConst = {
        ADMIN: 'admin',                                  // システム管理
        ORG_ADMIN: 'org_admin',                          // 組織内管理
        COMMON: 'common'                                 // 共通（システム管理・組織内管理）
    };

    /**
     * パラメータ管理パラメータ設定最大桁数定義
     */
    public static readonly PARAMETER_MAX_LENGTH: NumberConst = {
        FILE_EXTENSION: 256,                     // ファイル拡張子
        MAIL_TEXT: 2000,                         // メール通知定型文
        ATTACHMENT_DETAIL_FILE_EXTENSION: 256,   // 送付ファイルファイル拡張子
        MATERIAL_FILE_EXTENSION: 256,            // 教材ファイルファイル拡張子
        THUMBNAIL_FILE_EXTENSION: 256,           // サムネイルファイルファイル拡張子
        REPLY_FILE_EXTENSION: 256,               // 返信ファイルファイル拡張子
        APPLICATION_FILE_EXTENSION: 256,         // 申請ファイルファイル拡張子
        ATTACH_FILE_EXTENSION: 256,              // 添付ファイルファイル拡張子
        NOTIFICATION_DATA_TITLE: 100,            // 通知データタイトル
        NOTIFICATION_DATA_MESSAGE: 2000          // 通知データメッセージ
    };

    /**
     * パラメータ管理カテゴリ設定最大桁数定義
     */
     public static readonly CATEGORY_MAX_LENGTH: NumberConst = {
        // 発番カテゴリ
        NUMBER_PREFIX: 3,                   // 発番接頭辞
        NUMBER_PREFIX_CATEGORY_NAME: 256,   // カテゴリ名称
        DEPARTMENT_NAME: 256,               // 部門名
        TEL: 15,                            // 電話番号

        // メール通知先
        NOTIFICATION_NAME: 73,              // メール通知先名称
        MAIL: 254,                          // メールアドレス

        // 以下の機能の項目で使用する名称の最大桁数
        // ・校種設定：校種名称
        // ・教科設定：教科名称
        // ・科目設定：科目名称
        // ・コンテンツカテゴリ設定：カテゴリ名称
        // ・処理依頼内容設定：依頼内容名称
        CATEGORY_NAME: 50
    };

    /**
     * パラメータ管理パラメータ設定ファイル拡張子表示文字数定義
     * chipに表示するファイル拡張子の処理に使用
     */
    public static readonly DISPLAY_EXTENSION_LENGTH: number = 10;

    /**
     * パラメータ管理カテゴリ設定テーブル名定義
     * カテゴリの追加ボタンや削除の表示に使用
     */
    public static readonly CATEGORY_TABLE_NAME: StringConst = {
        NUMBER_PREFIX: '発番',
        NOTIFICATIONS: 'メール通知先',
        SCHOOL_TYPES: '校種',
        CURRICULUMS: '教科',
        SUBJECTS: '科目',
        CONTENT: 'カテゴリ',
        PROCESS_REQUEST: '依頼内容'
    };

    /**
     * 処理フラグ定義
     * パラメータ管理カテゴリ設定に使用
     */
    public static readonly PROCESS_CODE: NumberConst = {
        STORE: 1,                       // 新規
        UPDATE: 11,                     // 編集
        DELETE: 21                      // 削除
    };

    /**
     * パラメータ管理タブ番号
     */
    public static readonly TABS: StringConst = {
        FIRST: '1',                     // 1番目のタブ（各機能のパラメータ設定）
        SECOND: '2',                    // 2番目のタブ（公文書：発番カテゴリ設定、教材DB：校種設定、教育用コンテンツ：コンテンツカテゴリ設定、組織内管理：処理依頼内容設定）
        THIRD: '3',                     // 3番目のタブ（公文書：メール通知先設定、教材DB：教科設定）
        FOURTH: '4'                     // 4番目のタブ（教材DB：科目設定）
    };

    /**
     * 年度更新_人事異動データ変換
     * step2 一覧出力Excelファイルのファイル名
     */
    public static readonly PERSONNEL_CHANGE_EXCEL_FILE_NAME: string = '人事異動データ一覧.xlsx';

    /**
     * 年度更新_人事異動データ変換
     * step3 ファイル取得画面にて表示する、一括登録ファイルのファイル名
     */
    public static readonly PERSONNEL_CHANGE_BULK_FILE_NAME: string = '一括登録ファイル.zip';

    /**
     * 年度更新_通知データ作成
     * step3 一覧出力Excelファイルのファイル名
     */
    public static readonly NOTIFICATION_DATA_LIST_EXCEL_FILE_NAME: string = '通知データ一覧.xlsx';

    /**
     * 年度更新_通知データ作成
     * step3 ファイル取得画面にて表示する、基本データファイル、添付データファイルのファイル名
     */
    public static readonly NOTIFICATION_DATA_FILE_NAME: StringConst = {
        BASIC: '基本データファイル.xlsx',
        ATTACHMENT: '添付データファイル.zip'
    };

    /**
     * 年度更新_人事異動データ変換及び通知データ作成 共通
     * step3 ダウンロードの際のダイアログタイトルのメッセージ
     */
    public static readonly FISCAL_YEAR_UPDATE_FILE_DOWNLOAD_DIALOG_TITLE: StringConst = {
        LOADING: '読込中',
        DOWNLOADING: 'ダウンロード中'
    };

    /**
     * 年度更新_人事異動データ変換及び通知データ作成 共通
     * step3 初期表示前に表示するファイルサイズ
     */
    public static readonly FISCAL_YEAR_UPDATE_FILE_DOWNLOAD_FILE_SIZE_INIT: string = '―';

    /**
     * 年度更新_通知データ作成
     * step3 ファイル取得対象判別用パラメータ
     */
    public static readonly NOTIFICATION_FILE_DOWNLOAD_PARAMETER: StringConst = {
        BASIC: 'BASIC',
        ATTACHMENT: 'ATTACHMENT'
    };
}
